<!-- A panel that slides in from the right on wide screens and up from the bottom on phones. -->
<script lang="ts">
  import type { Snippet } from "svelte";

  let { title, onclose, children, footer }: { title: string; onclose: () => void; children: Snippet; footer?: Snippet } = $props();

  let panel: HTMLElement | undefined = $state();

  function portal(node: HTMLElement) {
    document.body.appendChild(node);
    return { destroy: () => node.remove() };
  }
  let opener: Element | null = null;

  $effect(() => {
    opener = document.activeElement;
    panel?.querySelector<HTMLElement>("input, select, textarea, button")?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onclose();
      if (e.key === "Tab" && panel) {
        const f = [...panel.querySelectorAll<HTMLElement>("a[href], button:not([disabled]), input, select, textarea")];
        const first = f[0];
        const last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last?.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first?.focus();
        }
      }
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
      (opener as HTMLElement | null)?.focus?.();
    };
  });
</script>

<!-- Moved to <body> so it always sits above the page, wherever it's opened from (e.g. a sticky sidebar). -->
<div class="portal" use:portal>
<div class="scrim" onclick={onclose} aria-hidden="true"></div>
<div class="panel" role="dialog" aria-modal="true" aria-label={title} bind:this={panel}>
  <header>
    <h2>{title}</h2>
    <button class="btn quiet small" onclick={onclose} aria-label="Close">Close</button>
  </header>
  <div class="body">{@render children()}</div>
  {#if footer}<footer>{@render footer()}</footer>{/if}
</div>
</div>

<style>
  .scrim {
    position: fixed;
    inset: 0;
    z-index: 60;
    background: rgb(17 23 42 / 0.36);
  }

  .panel {
    position: fixed;
    z-index: 61;
    top: 0;
    right: 0;
    bottom: 0;
    width: min(520px, 100vw);
    display: flex;
    flex-direction: column;
    background: var(--paper);
    box-shadow: var(--shadow-pop);
    animation: in-right 220ms cubic-bezier(0.2, 0.8, 0.2, 1);
  }

  header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 16px 20px;
    border-bottom: 1px solid var(--rule);
  }

  .body {
    flex: 1;
    overflow-y: auto;
    padding: 20px;
    overscroll-behavior: contain;
  }

  footer {
    display: flex;
    gap: 8px;
    align-items: center;
    padding: 12px 20px calc(env(safe-area-inset-bottom) + 12px);
    border-top: 1px solid var(--rule);
    background: var(--surface);
  }

  @keyframes in-right {
    from {
      transform: translateX(24px);
      opacity: 0;
    }
  }

  @keyframes in-up {
    from {
      transform: translateY(32px);
      opacity: 0;
    }
  }

  @media (max-width: 860px) {
    .panel {
      top: auto;
      left: 0;
      width: 100vw;
      max-height: 92vh;
      border-radius: var(--r-lg) var(--r-lg) 0 0;
      animation-name: in-up;
    }
  }
</style>
